import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SvgIconProps } from '@mui/material/SvgIcon';
import {
  TreeItem,
  TreeItemContentProps,
  TreeItemProps,
  treeItemClasses,
  useTreeItemState,
} from '@mui/x-tree-view/TreeItem';
import clsx from 'clsx';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';

// https://mui.com/x/react-tree-view/#gmail-clone
declare module 'react' {
  interface CSSProperties {
    '--tree-view-color'?: string;
    '--tree-view-bg-color'?: string;
  }
}

type StyledTreeItemProps = TreeItemProps & {
  bgColor?: string;
  bgColorForDarkMode?: string;
  color?: string;
  colorForDarkMode?: string;
  labelIcon?: React.ElementType<SvgIconProps>;
  labelInfo?: string;
  labelText: string;
  selectedOrgUnitId: string;
  setOpenEditOrgUnitDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenDeleteOrgUnitDialog: React.Dispatch<React.SetStateAction<boolean>>;
  directoryRoles: string[];
  limitedVisOrgList: string[];
  limitedVisEnabled: boolean;
  hasChildren?: boolean;
};

const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
  color: theme.palette.text.secondary,
  [`& .${treeItemClasses.content}`]: {
    color: theme.palette.text.secondary,
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    paddingRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    '&.Mui-expanded': {
      fontWeight: theme.typography.fontWeightRegular,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-selected, &.Mui-selected.Mui-focused': {
      backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
      color: 'var(--tree-view-color)',
    },
    [`& .${treeItemClasses.label}`]: {
      fontWeight: 'inherit',
      color: 'inherit',
    },
  },
  // [`& .${treeItemClasses.groupTransition}`]: {
  //   marginLeft: theme.spacing(1),
  //   [`& .${treeItemClasses.content}`]: {
  //     paddingLeft: theme.spacing(1),
  //   },
  // },
})) as unknown as typeof TreeItem;

export const StyledTreeItem = React.forwardRef(function StyledTreeItem(
  props: StyledTreeItemProps,
  ref: React.Ref<HTMLLIElement>
) {
  const theme = useTheme();
  const {
    bgColor,
    color,
    labelIcon: LabelIcon,
    labelInfo,
    selectedOrgUnitId,
    setOpenEditOrgUnitDialog,
    setOpenDeleteOrgUnitDialog,
    directoryRoles,
    labelText,
    colorForDarkMode,
    bgColorForDarkMode,
    limitedVisOrgList,
    limitedVisEnabled,
    itemId,
    hasChildren,
    ...other
  } = props;

  const styleProps = {
    '--tree-view-color':
      theme.palette.mode !== 'dark' ? color : colorForDarkMode,
    '--tree-view-bg-color':
      theme.palette.mode !== 'dark' ? bgColor : bgColorForDarkMode,
  };

  const CustomContent = React.forwardRef(function CustomContent(
    props: TreeItemContentProps,
    ref
  ) {
    const {
      classes,
      className,
      label,
      itemId,
      icon: iconProp,
      expansionIcon,
      displayIcon,
    } = props;

    const {
      disabled,
      expanded,
      selected,
      focused,
      handleExpansion,
      handleSelection,
      preventSelection,
    } = useTreeItemState(itemId);

    const icon = iconProp || expansionIcon || displayIcon;

    const handleMouseDown = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      preventSelection(event);
    };

    const handleExpansionClick = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      handleExpansion(event);
    };

    const handleSelectionClick = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      handleSelection(event);
    };

    return (
      <div
        className={clsx(className, classes.root, {
          [classes.expanded]: expanded,
          [classes.selected]: selected,
          [classes.focused]: focused,
          [classes.disabled]: disabled,
        })}
        onMouseDown={handleMouseDown}
        ref={ref as React.Ref<HTMLDivElement>}
      >
        <div onClick={handleExpansionClick} className={classes.iconContainer}>
          {icon}
        </div>
        <Typography
          onClick={handleSelectionClick}
          component="div"
          className={classes.label}
        >
          {label}
        </Typography>
      </div>
    );
  });

  return (
    <StyledTreeItemRoot
      itemId={itemId}
      ContentComponent={CustomContent}
      label={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            // normal height is 38
            height: 32,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
            {LabelIcon && <LabelIcon sx={{ color: 'inherit' }} />}
          </Box>

          <Stack direction="row" gap={1} sx={{ flexGrow: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'inherit',
                maxWidth: 'calc(100% - 10px)',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {labelText}
            </Typography>
            <Typography variant="caption" color="inherit">
              {labelInfo}
            </Typography>
          </Stack>
          {selectedOrgUnitId === itemId && itemId !== '/' && (
            <>
              <Tooltip title="Edit Organizational Unit" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenEditOrgUnitDialog(true);
                    }}
                    disabled={
                      !isAbleToSeeUIElement(
                        directoryRoles,
                        DirectoryRoles.EditOrgUnit.value
                      ) ||
                      (limitedVisEnabled &&
                        limitedVisOrgList.length > 0 &&
                        !limitedVisOrgList.includes(itemId))
                    }
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {!hasChildren ? (
                <Tooltip title="Delete Organizational Unit" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDeleteOrgUnitDialog(true);
                      }}
                      disabled={
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.RemoveOrgUnit.value
                        ) ||
                        (limitedVisEnabled &&
                          limitedVisOrgList.length > 0 &&
                          !limitedVisOrgList.includes(itemId))
                      }
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </>
          )}
        </Box>
      }
      style={styleProps}
      {...other}
      ref={ref}
    />
  );
});
